import React, { useEffect, useState, useRef } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Modal, Field, Input, Textarea, Dot, Av, Select } from '../ui.jsx';
import { ContactPicker } from '../widgets/ContactPicker.jsx';
import { QuickCreatePanel } from '../widgets/QuickCreateFlow.jsx';
import { HelpWidget } from '../widgets/HelpWidget.jsx';
import { PhotoPicker } from '../widgets/PhotoPicker.jsx';
import { TechOnboardingTour } from '../widgets/TechOnboardingTour.jsx';
import { TeamChat } from './TeamChat.jsx';

const STATUS_COLOR = { Scheduled:G.blue, Booked:G.cyan, Dispatched:G.purple, 'En Route':G.cyan, 'On Site':G.orange, Completed:G.green, 'Needs Attention':G.yellow, Cancelled:G.red };
const NEXT = { Booked:'Dispatched', Scheduled:'Dispatched', Dispatched:'En Route', 'En Route':'On Site', 'On Site':'Completed' };

// Haversine distance in km between two lat/lng pairs.
function haversineKm(a, b) {
  const R = 6371; // km
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat/2)**2 + Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
// Cache geocoded addresses in sessionStorage so we don't pound Nominatim on
// every 60s GPS ping. Returns null if address can't be geocoded.
const geocodeCache = {};
async function geocodeAddress(address) {
  if (!address) return null;
  const key = `geo:${address}`;
  if (geocodeCache[key]) return geocodeCache[key];
  try { const cached = sessionStorage.getItem(key); if (cached) { const j = JSON.parse(cached); geocodeCache[key] = j; return j; }} catch {}
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`);
    const data = await r.json();
    if (!Array.isArray(data) || !data[0]) return null;
    const point = { lat: Number(data[0].lat), lng: Number(data[0].lon) };
    geocodeCache[key] = point;
    try { sessionStorage.setItem(key, JSON.stringify(point)); } catch {}
    return point;
  } catch { return null; }
}
// Returns distance in km between a GPS coords object and a postal address.
async function distanceToAddress(coords, address) {
  const target = await geocodeAddress(address);
  if (!target) return null;
  return haversineKm({ lat: coords.latitude, lng: coords.longitude }, target);
}

export function TechApp({ user, onLogout, toast, previewingAs, onExitPreview }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [prevan, setPrevan] = useState(null);
  const [showPrevan, setShowPrevan] = useState(false);
  const [view, setView] = useState('today'); // 'today' | 'customers' | 'create' | 'leaderboard'
  const [dateRange, setDateRange] = useState('today'); // 'today' | 'tomorrow' | 'week'
  const [runningLateOpen, setRunningLateOpen] = useState(false);
  const [sosOpen, setSosOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  const load = () => api('/api/tech/me').then(setData).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  const loadPrevan = () => api('/api/tech/prevan').then(setPrevan).catch(()=>{});
  useEffect(() => { load(); loadPrevan(); const t = setInterval(load, 30000); return ()=>clearInterval(t); }, []);

  // Chat unread badge — cheap poll every 30s. Not tied to whether the overlay
  // is open; techs need to see "🔴 new message from Kyle" while on a job.
  useEffect(() => {
    let alive = true;
    const check = () => api('/api/team-chat/channels').then(r => {
      if (!alive) return;
      const total = (r.channels || []).reduce((n, c) => n + (c.unread || 0), 0);
      setChatUnread(total);
    }).catch(()=>{});
    check();
    const t = setInterval(check, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Live location ping — fires every 60s while the tech is clocked in so the
  // dispatcher map shows where they are. Silently no-ops if denied.
  useEffect(() => {
    if (!data?.openTimesheet) return;
    if (!('geolocation' in navigator)) return;
    let cancelled = false;
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          api('/api/tech/location', { method:'POST', body:{ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }}).catch(()=>{});
          // Geofence auto-arrival — if a job is En Route and the tech's GPS
          // is within ~150m of its address (resolved via cached geocode), flip
          // it to On Site silently. Saves the manual tap + makes on-time %
          // accurate. The server is idempotent so a duplicate call is safe.
          try {
            const enroute = (data?.jobs || []).find(j => j.status === 'En Route' && j.address);
            if (enroute) {
              const dist = await distanceToAddress(pos.coords, enroute.address);
              if (dist !== null && dist < 0.15) { // 150 meters
                const r = await api(`/api/tech/jobs/${enroute.id}/geofence-arrival`, { method:'POST', body:{}}).catch(()=>null);
                if (r?.autoArrival) { toast('📍 On site auto-detected'); load(); }
              }
            }
          } catch {}
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 15000 }
      );
    };
    ping();
    const t = setInterval(ping, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [data?.openTimesheet?.id, data?.jobs]);

  const [clockKindOpen, setClockKindOpen] = useState(false);
  const clock = async () => {
    // Clocking IN: ask what kind. Clocking OUT: just close the segment.
    if (!data?.openTimesheet) { setClockKindOpen(true); return; }
    try { await api('/api/tech/clock', { method:'POST', body:{} }); load(); toast('Clocked out'); }
    catch(e) { toast(e.message,'error'); }
  };
  const clockInWithKind = async (kind) => {
    setClockKindOpen(false);
    try { await api('/api/tech/clock', { method:'POST', body:{ type: kind } }); load(); toast(`Clocked in · ${kind}`); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:G.bg }}><Spinner sz={32}/></div>;

  if (!data || !data.tech) return <div style={{ padding:24, textAlign:'center', color:G.textMuted }}>
    <div style={{ ...styles.card, maxWidth:420, margin:'80px auto', padding:32 }}>
      <h2 style={{ margin:0 }}>{previewingAs ? 'Tech preview' : 'No tech profile'}</h2>
      <div style={{ marginTop:8 }}>
        {previewingAs
          ? "This is exactly what your field techs see on their phones. Add yourself in Team → Field Techs (linked to your user) to load real data here, or attach this preview to any tech below."
          : "Your account is set to \"tech\" but no field-tech record exists. Ask your dispatcher to add you in Team → Field Techs."}
      </div>
      {previewingAs && onExitPreview && <Btn v="primary" style={{ marginTop:14 }} onClick={onExitPreview}>Exit preview</Btn>}
      <Btn v="ghost" style={{ marginTop:14, marginLeft:8 }} onClick={onLogout}>Logout</Btn>
    </div>
  </div>;

  const { tech, jobs, openTimesheet } = data;
  const nowJobs = jobs.filter(j => ['En Route','On Site','Dispatched'].includes(j.status));
  const today = new Date();
  const todayKey = today.toISOString().slice(0,10);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const tomorrowKey = tomorrow.toISOString().slice(0,10);
  const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
  const weekEndKey = weekEnd.toISOString().slice(0,10);

  const todayJobs = jobs.filter(j => (j.scheduledDate||'').slice(0,10) === todayKey);
  const tomorrowJobs = jobs.filter(j => (j.scheduledDate||'').slice(0,10) === tomorrowKey);
  const weekJobs = jobs.filter(j => {
    const d = (j.scheduledDate||'').slice(0,10);
    return d && d > todayKey && d <= weekEndKey;
  });

  // Range toggle drives what shows in the main section.
  const rangedJobs = dateRange === 'today' ? todayJobs : dateRange === 'tomorrow' ? tomorrowJobs : weekJobs;
  const rangeLabel = dateRange === 'today' ? 'Today' : dateRange === 'tomorrow' ? 'Tomorrow' : 'This week';
  // Group week view by date for readability.
  const weekGroups = (() => {
    if (dateRange !== 'week') return null;
    const out = {};
    for (const j of weekJobs) {
      const k = (j.scheduledDate||'').slice(0,10);
      out[k] = out[k] || [];
      out[k].push(j);
    }
    return out;
  })();

  const exitImpersonation = async () => {
    try { await api('/api/auth/unjump-user', { method:'POST' }); window.location.reload(); }
    catch(e) { toast(e.message, 'error'); }
  };

  return <div style={{ minHeight:'100vh', background:G.bg, color:G.text, paddingBottom:72 }}>
    <TechOnboardingTour/>
    {previewingAs && onExitPreview && <div style={{ background:G.purple+'22', borderBottom:`1px solid ${G.purple}66`, padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, fontSize:12 }}>
      <span style={{ color:G.purple, fontWeight:700 }}>Preview as tech — you're still authenticated as {previewingAs}. All actions still run under your account.</span>
      <Btn sz="xs" v="purple" onClick={onExitPreview}>Exit preview</Btn>
    </div>}
    {user?.impersonating && <div style={{ background:G.purple+'22', borderBottom:`1px solid ${G.purple}66`, padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, fontSize:12 }}>
      <span style={{ color:G.purple, fontWeight:700 }}>Viewing as {tech.name} — read-only preview from your admin session</span>
      <Btn sz="xs" v="purple" onClick={exitImpersonation}>Exit tech view</Btn>
    </div>}
    <div style={{ position:'sticky', top:0, zIndex:10, background:G.sidebar, borderBottom:`1px solid ${G.border}`, padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <Av name={tech.name} sz={36}/>
        <div>
          <div style={{ fontWeight:700, fontSize:14 }}>{tech.name}</div>
          <div style={{ fontSize:11, color:G.textMuted }}>{tech.role} · {fmtUsd(tech.hourlyRate||0)}/hr</div>
        </div>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <button onClick={()=>{ setChatOpen(true); setChatUnread(0); }}
          title="Team Chat"
          style={{
            position:'relative', width:40, height:40, borderRadius:20,
            border:`1px solid ${chatUnread>0 ? G.purple : G.border}`,
            background: chatUnread>0 ? G.purpleDim : 'transparent',
            color: chatUnread>0 ? G.purple : G.text,
            fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
          }}>
          💬
          {chatUnread > 0 && <span style={{
            position:'absolute', top:-4, right:-4, background:G.red, color:'#fff',
            fontSize:10, fontWeight:800, padding:'2px 5px', borderRadius:10, minWidth:16, textAlign:'center',
          }}>{chatUnread}</span>}
        </button>
        <Btn sz="sm" v="ghost" onClick={user?.impersonating ? exitImpersonation : onLogout}>{user?.impersonating ? 'Exit' : 'Logout'}</Btn>
      </div>
    </div>

    {chatOpen && <div style={{
      position:'fixed', inset:0, zIndex:9999, background:G.bg,
      display:'flex', flexDirection:'column',
    }}>
      <div style={{
        padding:'10px 14px', background:G.sidebar, borderBottom:`1px solid ${G.border}`,
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <div style={{ fontWeight:800, fontSize:16 }}>💬 Team Chat</div>
        <button onClick={()=>setChatOpen(false)} style={{
          background:'none', border:`1px solid ${G.border}`, color:G.text,
          padding:'6px 14px', borderRadius:8, fontSize:14, cursor:'pointer', fontWeight:700,
        }}>× Close</button>
      </div>
      <div style={{ flex:1, minHeight:0 }}>
        <TeamChat user={user} toast={toast} embedded/>
      </div>
    </div>}

    {view === 'customers' && <CustomerSearch toast={toast} onOpenContact={()=>{}} />}
    {view === 'leaderboard' && <TechLeaderboard toast={toast}/>}
    {view === 'training' && <TechTraining toast={toast}/>}
    {view === 'me' && <TechMePanel toast={toast}/>}

    {view === 'create' && <div style={{ padding:16, paddingBottom:90 }}>
      <QuickCreatePanel techMode toast={toast} onCreated={() => { load(); setView('today'); }}/>
    </div>}

    {view === 'today' && <>
    {/* Daily summary card — gives the tech a quick read on today's load */}
    {data.summary && <DailySummaryCard summary={data.summary} onRunningLate={()=>setRunningLateOpen(true)}/>}
    {/* Clock-in pill */}
    <div style={{ padding:'14px 18px', display:'flex', flexDirection:'column', gap:10 }}>
      <div onClick={clock} style={{ ...styles.card, padding:16, cursor:'pointer', borderColor: openTimesheet ? G.green+'66' : G.cardBorder, background: openTimesheet ? G.greenLight : G.card, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:12, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{openTimesheet ? '🟢 Clocked In' : 'Clock'}</div>
          <div style={{ fontSize:20, fontWeight:800, color: openTimesheet?G.green:G.text }}>{openTimesheet ? 'Tap to Clock Out' : 'Tap to Clock In'}</div>
          {openTimesheet && <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Started {new Date(openTimesheet.clockedInAt).toLocaleTimeString()}</div>}
        </div>
        <div style={{ fontSize:36 }}>{openTimesheet ? '⏸️' : '▶️'}</div>
      </div>
      {prevan?.jobs?.length > 0 && (prevan.isTomorrow ? (
        // Evening view — expanded so techs can load their van without tapping.
        <div style={{ ...styles.card, padding:14, borderColor: G.orange+'88', background: G.orange+'11' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div>
              <div style={{ fontSize:11, color:G.orange, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em' }}>🌙 Load Your Van For Tomorrow</div>
              <div style={{ fontSize:16, fontWeight:800, marginTop:2 }}>📋 {prevan.jobs.length} job{prevan.jobs.length>1?'s':''} · {prevan.consolidatedPartsList.length} parts</div>
            </div>
            <button onClick={()=>setShowPrevan(true)} style={{ background:G.orange, color:'#fff', border:'none', padding:'8px 14px', borderRadius:8, fontWeight:800, cursor:'pointer', fontSize:13 }}>Open</button>
          </div>
          {prevan.consolidatedPartsList.length > 0 ? (
            <div style={{ maxHeight:220, overflow:'auto', background:G.card, borderRadius:8, padding:8 }}>
              {prevan.consolidatedPartsList.slice(0, 20).map((p, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', gap:8, padding:'6px 8px', fontSize:13, borderBottom: i<prevan.consolidatedPartsList.length-1?`1px solid ${G.border}`:'none' }}>
                  <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name || p.description || p.sku}</span>
                  <strong style={{ color:G.orange, whiteSpace:'nowrap' }}>× {p.qty || p.quantity || 1}</strong>
                </div>
              ))}
              {prevan.consolidatedPartsList.length > 20 && <div style={{ fontSize:11, color:G.textMuted, padding:'6px 8px' }}>+ {prevan.consolidatedPartsList.length - 20} more — tap Open to see full list</div>}
            </div>
          ) : (
            <div style={{ fontSize:12, color:G.textMuted, padding:'8px 4px' }}>No parts assigned to tomorrow's jobs. Check the linked estimates to make sure line items were saved.</div>
          )}
        </div>
      ) : (
        <div onClick={()=>setShowPrevan(true)} style={{ ...styles.card, padding:14, cursor:'pointer', borderColor: G.cyan+'66', background: G.cyanLight, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Today's Pre-Van</div>
            <div style={{ fontSize:16, fontWeight:700, color:G.cyan }}>📋 {prevan.jobs.length} job{prevan.jobs.length>1?'s':''} · {prevan.consolidatedPartsList.length} parts to load</div>
          </div>
          <div style={{ fontSize:24 }}>📦</div>
        </div>
      ))}
    </div>

    {nowJobs.length > 0 && <Section label="🚐 In Progress">
      {nowJobs.map(j => <JobCard key={j.id} j={j} onOpen={()=>setActive(j)} highlight/>)}
    </Section>}

    {/* Map / List view toggle for today's stops */}
    <RouteToggle showMap={showMap} setShowMap={setShowMap}/>
    {showMap && <RouteMapView jobs={(jobs||[]).filter(j => j.scheduledDate === today)}/>}

    {/* Date-range toggle: Today / Tomorrow / This week. Lets techs glance
        forward without leaving the home screen. */}
    <div style={{ padding:'0 18px 4px', display:'flex', gap:6 }}>
      {[
        { id:'today',    label:'Today',     count: todayJobs.length },
        { id:'tomorrow', label:'Tomorrow',  count: tomorrowJobs.length },
        { id:'week',     label:'This week', count: weekJobs.length },
      ].map(t => (
        <button key={t.id} onClick={()=>setDateRange(t.id)} style={{
          flex:1, padding:'10px 8px', borderRadius:10,
          background: dateRange===t.id ? G.purple : G.input,
          color: dateRange===t.id ? '#fff' : G.text,
          border:`1px solid ${dateRange===t.id ? G.purple : G.cardBorder}`,
          fontSize:13, fontWeight:700, cursor:'pointer',
          display:'flex', flexDirection:'column', alignItems:'center', gap:2,
        }}>
          <span>{t.label}</span>
          <span style={{ fontSize:11, opacity:.85 }}>{t.count} job{t.count===1?'':'s'}</span>
        </button>
      ))}
    </div>

    {dateRange !== 'week' ? (
      rangedJobs.length > 0 ? (
        <Section label={`${rangeLabel} (${rangedJobs.length})`}>
          {rangedJobs.map(j => <JobCard key={j.id} j={j} onOpen={()=>setActive(j)}/>)}
        </Section>
      ) : (
        <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>
          No jobs {dateRange === 'tomorrow' ? 'for tomorrow' : 'today'}.
        </div>
      )
    ) : (
      weekJobs.length > 0 ? (
        Object.keys(weekGroups).sort().map(dateKey => {
          const d = new Date(dateKey + 'T12:00:00');
          const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          return <Section key={dateKey} label={`${label} (${weekGroups[dateKey].length})`}>
            {weekGroups[dateKey].map(j => <JobCard key={j.id} j={j} onOpen={()=>setActive(j)}/>)}
          </Section>;
        })
      ) : (
        <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>No jobs this week.</div>
      )
    )}

    {jobs.length === 0 && <div style={{ padding:40, textAlign:'center', color:G.textMuted }}>
      <div>No jobs assigned. Enjoy the day!</div>
    </div>}

    {/* Tomorrow preview at the bottom of Today view */}
    {dateRange === 'today' && <TomorrowPreview/>}

    </>}

    <JobDrawer job={active} onClose={()=>setActive(null)} onChanged={load} toast={toast} accountIndustry={data?.account?.industry || user?.account?.industry}/>
    <PrevanModal open={showPrevan} data={prevan} onClose={()=>setShowPrevan(false)}/>
    {runningLateOpen && <RunningLateModal onClose={()=>setRunningLateOpen(false)} toast={toast}/>}
    {sosOpen && <SOSModal onClose={()=>setSosOpen(false)} toast={toast}/>}
    {clockKindOpen && <ClockKindModal onClose={()=>setClockKindOpen(false)} onPick={clockInWithKind}/>}
    {/* Floating SOS button — always reachable on every tech screen */}
    <button onClick={()=>setSosOpen(true)} title="Need help" style={{
      position:'fixed', top:14, right:14, zIndex:30,
      width:44, height:44, borderRadius:22, border:'none',
      background:G.red, color:'#fff', cursor:'pointer',
      fontSize:18, fontWeight:800, boxShadow:'0 4px 10px rgba(0,0,0,.3)',
    }}>🆘</button>

    {/* Help bubble — Ask AI + FAQ tabs. Lifted above the bottom nav (84px) and
        pinned bottom-left so it doesn't collide with the future Quick Create
        FAB on the right. */}
    <HelpWidget currentPage={`tech-portal:${view}`} bottomOffset={84} leftOffset={16}/>

    {/* Bottom nav — fixed, thumb-reachable */}
    <div style={{ position:'fixed', bottom:0, left:0, right:0, background:G.sidebar, borderTop:`1px solid ${G.border}`,
                  display:'flex', zIndex:20, paddingBottom:'env(safe-area-inset-bottom, 0)' }}>
      {[
        { id:'today',       label:'Today',     icon:'📅' },
        { id:'create',      label:'Create',    icon:'➕' },
        { id:'customers',   label:'Customers', icon:'👥' },
        { id:'training',    label:'Training',  icon:'🎓' },
        { id:'me',          label:'Me',        icon:'⚙️' },
      ].map(t => (
        <button key={t.id} onClick={()=>setView(t.id)} style={{
          flex:1, padding:'12px 4px', background:'transparent', border:'none', cursor:'pointer',
          color: view === t.id ? G.purple : G.textMuted, fontSize:11, fontWeight:600,
          display:'flex', flexDirection:'column', alignItems:'center', gap:2,
        }}>
          <span style={{ fontSize:20 }}>{t.icon}</span>
          {t.label}
        </button>
      ))}
    </div>
  </div>;
}

// Tech-facing leaderboard. Shows production metrics only — NO commission $
// amounts (those are owner-only). Highlights the logged-in tech's own row.
// Clock-in kind picker. Tech taps a category so off-job time (driving,
// admin, lunch, training) is bucketed correctly for utilization analysis.
function ClockKindModal({ onClose, onPick }) {
  const OPTIONS = [
    { id:'job',      label:'🔧 On a Job',  sub:'Billable work' },
    { id:'driving',  label:'🚐 Driving',   sub:'Between stops' },
    { id:'admin',    label:'📋 Admin',     sub:'Paperwork, parts run' },
    { id:'lunch',    label:'🍴 Lunch',     sub:'Unpaid break' },
    { id:'training', label:'🎓 Training',  sub:'Learning or shadowing' },
  ];
  return <Modal open onClose={onClose} width={420}>
    <h3 style={{ margin:'0 0 8px' }}>What are you working on?</h3>
    <p style={{ fontSize:12, color:G.textMuted, marginBottom:14 }}>This buckets your time so the office can see billable vs non-billable hours.</p>
    {OPTIONS.map(o => (
      <button key={o.id} onClick={()=>onPick(o.id)} style={{
        display:'block', width:'100%', textAlign:'left',
        padding:'12px 14px', borderRadius:10, marginBottom:8,
        background: G.input, border:`1px solid ${G.cardBorder}`, cursor:'pointer',
      }}>
        <div style={{ fontSize:14, fontWeight:700 }}>{o.label}</div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{o.sub}</div>
      </button>
    ))}
    <Btn v="ghost" full onClick={onClose} style={{ marginTop:6 }}>Cancel</Btn>
  </Modal>;
}

// Route view toggle (Map / List) for today's stops
function RouteToggle({ showMap, setShowMap }) {
  return <div style={{ padding:'0 18px 8px', display:'flex', gap:6 }}>
    <button onClick={()=>setShowMap(false)} style={{
      flex:1, padding:'8px 4px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
      border:`1px solid ${!showMap?G.purple:G.cardBorder}`,
      background: !showMap ? G.purpleDim : 'transparent', color: !showMap ? G.purple : G.textMuted,
    }}>📋 List</button>
    <button onClick={()=>setShowMap(true)} style={{
      flex:1, padding:'8px 4px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
      border:`1px solid ${showMap?G.purple:G.cardBorder}`,
      background: showMap ? G.purpleDim : 'transparent', color: showMap ? G.purple : G.textMuted,
    }}>🗺 Map</button>
  </div>;
}

// Lightweight multi-stop route map using Leaflet via CDN. Loads the script
// + CSS once on mount, geocodes each stop's address (cached), renders a
// marker per stop with a numbered label and a polyline connecting them.
function RouteMapView({ jobs }) {
  const mapRef = useRef(null);
  const [ready, setReady] = useState(typeof window !== 'undefined' && !!window.L);
  useEffect(() => {
    if (window.L) { setReady(true); return; }
    const css = document.createElement('link'); css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    const js = document.createElement('script'); js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    js.onload = () => setReady(true);
    document.head.appendChild(css); document.head.appendChild(js);
  }, []);
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const L = window.L;
    while (mapRef.current.firstChild) mapRef.current.removeChild(mapRef.current.firstChild);
    const map = L.map(mapRef.current).setView([39.5, -98.35], 4); // continental US fallback
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution:'&copy; OSM', maxZoom: 19 }).addTo(map);
    let cancelled = false;
    const points = [];
    (async () => {
      for (let i = 0; i < jobs.length; i++) {
        const j = jobs[i];
        if (!j.address) continue;
        const geo = await geocodeAddress(j.address);
        if (!geo || cancelled) continue;
        points.push([geo.lat, geo.lng]);
        const icon = L.divIcon({
          className:'route-pin',
          html:`<div style="background:#ff6a3d;color:#fff;font-weight:700;border-radius:18px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.3);font-size:12px;">${i+1}</div>`,
          iconSize:[30,30], iconAnchor:[15,15],
        });
        L.marker([geo.lat, geo.lng], { icon }).addTo(map)
          .bindPopup(`<strong>${j.startTime||''}</strong> · ${j.contact?.firstName || ''}<br>${j.address}`);
      }
      if (points.length >= 2) L.polyline(points, { color:'#ff6a3d', weight:3, opacity:.7 }).addTo(map);
      if (points.length) map.fitBounds(points, { padding:[40,40] });
    })();
    return () => { cancelled = true; map.remove(); };
  }, [ready, jobs.map(j=>j.id).join('|')]);
  if (!jobs.length) return <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>No stops today to map.</div>;
  return <div style={{ padding:'0 18px 14px' }}>
    <div ref={mapRef} style={{ height:300, borderRadius:10, overflow:'hidden', border:`1px solid ${G.cardBorder}` }}/>
    {!ready && <div style={{ marginTop:8, fontSize:11, color:G.textMuted, textAlign:'center' }}>Loading map…</div>}
  </div>;
}

// Diagnostic checklist — service-type-specific. Each item is a tappable
// checkbox; state persists on the job row server-side.
// Mandatory Service Call Forms. Every field the account's admin marked as
// required has to be filled before the tech can advance the job to Completed.
// Auto-saves on blur; auto-scrolls to first missing field on submit-blocked.
function ServiceCallFormsPanel({ jobId, toast }) {
  const [forms, setForms] = React.useState(null);
  const [values, setValues] = React.useState({});
  const [expandedFormId, setExpandedFormId] = React.useState(null);
  const [saving, setSaving] = React.useState({});
  const [loadErr, setLoadErr] = React.useState(null);
  // AI package builder — available on any account that has a pricebook AND
  // a call-form with a tier_table section. Capability checked once per job.
  const [emAiEnabled, setEmAiEnabled] = React.useState(false);
  React.useEffect(() => {
    let cancel = false;
    api(`/api/em-packages/capability/${jobId}`)
      .then(r => { if (!cancel) setEmAiEnabled(!!r?.enabled); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [jobId]);
  const load = React.useCallback(() => {
    setLoadErr(null);
    api(`/api/call-forms/for-job/${jobId}`).then(r => {
      setForms(r.forms || []);
      const initV = {};
      (r.forms || []).forEach(f => { initV[f.form.id] = f.response.values || {}; });
      setValues(initV);
      if ((r.forms || []).length > 0 && !expandedFormId) {
        const incomplete = r.forms.find(f => !f.response.isComplete);
        if (incomplete) setExpandedFormId(incomplete.form.id);
      }
    }).catch(e => { setLoadErr(e?.message || 'Failed to load form'); setForms([]); });
  }, [jobId]);
  React.useEffect(() => { load(); }, [load]);

  const saveForm = async (formId) => {
    setSaving(s => ({ ...s, [formId]: true }));
    try {
      await api(`/api/call-forms/response/${formId}/${jobId}`, { method:'PATCH', body:{ values: values[formId] || {} }});
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(s => ({ ...s, [formId]: false })); }
  };
  const setVal = (formId, key, v) => {
    setValues(prev => ({ ...prev, [formId]: { ...(prev[formId] || {}), [key]: v }}));
  };

  if (!forms) return null;

  // Diagnostic pill when zero forms are attached — helps Kyle + agency admins
  // tell the difference between "panel didn't mount" (silent bug) and "this
  // account has no mandatory forms configured yet" (setup gap).
  if (forms.length === 0) {
    return <div style={{ ...styles.card, padding:14, marginBottom:10, border:`2px dashed ${loadErr?G.red:G.orange}88`, background:(loadErr?G.red:G.orange)+'11' }}>
      <div style={{ fontSize:11, color:loadErr?G.red:G.orange, fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>{loadErr ? `⚠ Load error: ${loadErr}` : '⚠ No mandatory forms attached'}</div>
      <div style={{ fontSize:13, color:G.text, lineHeight:1.5 }}>
        This account has no service-call forms configured. An agency admin can attach one in
        <strong> Agency → this account → ⚡ Vertical</strong> (or <strong>🚫 Call Forms</strong>).
      </div>
    </div>;
  }

  return <>{forms.map(({ form, response, progress }) => {
    const isExp = expandedFormId === form.id;
    const complete = response.isComplete;
    const missingN = progress.missingCount;
    const v = values[form.id] || {};
    return <div key={form.id} style={{ ...styles.card, padding:0, marginBottom:10, overflow:'hidden', border:`2px solid ${complete?G.green:G.red}` }}>
      <div onClick={()=>setExpandedFormId(isExp ? null : form.id)}
        style={{ padding:'12px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center',
                 background: complete ? G.green+'11' : G.red+'11' }}>
        <div>
          <div style={{ fontSize:11, color: complete?G.green:G.red, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>
            {complete ? '✓ COMPLETE' : `⚠ MANDATORY · ${missingN} FIELD${missingN===1?'':'S'} MISSING`}
          </div>
          <div style={{ fontSize:14, fontWeight:700, marginTop:2 }}>{form.name}</div>
        </div>
        <span style={{ fontSize:18, color:G.textMuted }}>{isExp ? '▲' : '▼'}</span>
      </div>
      {isExp && <div style={{ padding:14 }}>
        {form.description && <div style={{ fontSize:12, color:G.textMuted, marginBottom:12, lineHeight:1.5, padding:'8px 12px', background:G.input, borderRadius:6 }}>{form.description}</div>}
        {(form.schema.sections || []).map(section => (
          <div key={section.id} style={{ marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${G.border}` }}>
            <div style={{ fontSize:13, fontWeight:800, color:G.text, marginBottom:8, textTransform:'uppercase', letterSpacing:'.03em' }}>{section.title}</div>
            {section.type === 'instructions' ? (
              <ol style={{ paddingLeft:20, margin:0 }}>
                {section.steps.map((s, i) => <li key={i} style={{ fontSize:13, lineHeight:1.6, color:G.text, marginBottom:4 }}>{s}</li>)}
              </ol>
            ) : section.type === 'tier_table' ? (
              <>
                {emAiEnabled && (
                  <EmPackageBuilder
                    jobId={jobId}
                    formId={form.id}
                    values={v}
                    setValues={(bareMapping) => {
                      // AI returns BARE keys (line.0.desc, total.total.0, ...).
                      // Prefix with this specific tier_table section's id so
                      // qca_proposal (Electric Medic), hvac_diag, or any other
                      // vertical form's tier_table renders pre-filled.
                      const prefixed = {};
                      Object.keys(bareMapping).forEach(k => { prefixed[`${section.id}.${k}`] = bareMapping[k]; });
                      const next = { ...v, ...prefixed };
                      Object.keys(prefixed).forEach(k => setVal(form.id, k, prefixed[k]));
                      api(`/api/call-forms/response/${form.id}/${jobId}`, { method:'PATCH', body:{ values: next }}).catch(()=>{});
                    }}
                    toast={toast}/>
                )}
                <TierTableField section={section} formId={form.id} values={v} setVal={setVal}/>
              </>
            ) : section.type === 'checklist_before_after' ? (
              <ChecklistBeforeAfterField section={section} formId={form.id} values={v} setVal={setVal} onSave={()=>saveForm(form.id)} toast={toast}/>
            ) : (
              (section.fields || []).map(f => {
                const key = `${section.id}.${f.id}`;
                const missing = f.required && !hasVal(v[key]);
                return <div key={f.id} style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color: missing ? G.red : G.text, marginBottom:4 }}>
                    {f.label}{f.required && ' *'}
                  </label>
                  {f.type === 'text' && <Input value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)} placeholder={f.placeholder||''}/>}
                  {f.type === 'textarea' && <Textarea value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)} style={{ minHeight:60 }}/>}
                  {f.type === 'number' && <Input type="number" inputMode="decimal" value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)}/>}
                  {f.type === 'yesno' && <div style={{ display:'flex', gap:8 }}>
                    {['Yes','No'].map(opt => <button key={opt} onClick={()=>{ setVal(form.id, key, opt); setTimeout(()=>saveForm(form.id), 100); }}
                      style={{ flex:1, padding:'10px 14px', borderRadius:8, border:`1px solid ${v[key]===opt?G.green:G.border}`, background:v[key]===opt?G.green+'22':G.card, color:v[key]===opt?G.green:G.text, fontWeight:700, cursor:'pointer' }}>{opt}</button>)}
                  </div>}
                  {f.type === 'select' && <Select value={v[key]||''} onChange={e=>{ setVal(form.id, key, e.target.value); setTimeout(()=>saveForm(form.id), 100); }}>
                    <option value="">Pick one…</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </Select>}
                </div>;
              })
            )}
          </div>
        ))}
        <Btn v="primary" onClick={()=>saveForm(form.id)} disabled={saving[form.id]} full>{saving[form.id] ? 'Saving…' : complete ? '✓ Save & Confirm Complete' : 'Save Progress'}</Btn>
      </div>}
    </div>;
  })}</>;
}

function hasVal(v) { if (v == null) return false; if (typeof v === 'string') return v.trim().length > 0; if (typeof v === 'number') return !Number.isNaN(v); return true; }

// Renders a Before/After checklist section. Each half has:
//   - a checkbox list (each item is boolean)
//   - a single photo upload (uses /api/photos generic uploader)
// Values live in the same form's values object, keyed by `${section.id}.before.<itemId>`
// or `${section.id}.before.photo` (URL string).
function ChecklistBeforeAfterField({ section, formId, values, setVal, onSave, toast }) {
  const halves = [
    section.before ? { key: 'before', label: 'BEFORE — Arrival', cfg: section.before, tint: G.orange } : null,
    section.after  ? { key: 'after',  label: 'AFTER — Departure', cfg: section.after,  tint: G.green  } : null,
  ].filter(Boolean);
  return <div style={{ display:'grid', gap:12 }}>
    {halves.map(h => (
      <div key={h.key} style={{ border:`1px solid ${h.tint}44`, background:h.tint+'0a', borderRadius:10, padding:12 }}>
        <div style={{ fontSize:12, fontWeight:800, color:h.tint, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>{h.label}</div>
        {(h.cfg.checklist || []).map(item => {
          const key = `${section.id}.${h.key}.${item.id}`;
          const checked = values[key] === true;
          return <div key={item.id} onClick={()=>{ setVal(formId, key, !checked); setTimeout(onSave, 100); }}
            style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'8px 4px', cursor:'pointer', minHeight:44 }}>
            <div style={{ width:24, height:24, borderRadius:6, border:`2px solid ${checked?h.tint:G.border}`,
              background: checked ? h.tint : 'transparent', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800, fontSize:15 }}>
              {checked ? '✓' : ''}
            </div>
            <div style={{ flex:1, fontSize:14, color: checked ? G.textMuted : G.text, textDecoration: checked ? 'line-through' : 'none', paddingTop:2 }}>
              {item.label}{item.required && !checked && <span style={{ color:G.red }}> *</span>}
            </div>
          </div>;
        })}
        <PhotoUploadField
          formId={formId}
          valueKey={`${section.id}.${h.key}.photo`}
          currentUrl={values[`${section.id}.${h.key}.photo`]}
          label={h.cfg.photoLabel || `${h.key === 'before' ? 'Before' : 'After'} Photo`}
          required={h.cfg.photoRequired}
          tint={h.tint}
          setVal={setVal}
          onSaved={onSave}
          toast={toast}/>
      </div>
    ))}
  </div>;
}

// Small uploader — file input, sends to /api/photos, stores the returned URL
// in the form's values under the given valueKey. Shows the current photo if
// present (thumbnail with "replace" button).
function PhotoUploadField({ formId, valueKey, currentUrl, label, required, tint, setVal, onSaved, toast }) {
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(String(rd.result || ''));
        rd.onerror = () => rej(new Error('read failed'));
        rd.readAsDataURL(file);
      });
      const r = await fetch('/api/uploads', {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ name: file.name, mime: file.type, base64 }),
      });
      if (!r.ok) throw new Error(`upload failed (${r.status})`);
      const j = await r.json();
      if (!j.url) throw new Error('server did not return url');
      setVal(formId, valueKey, j.url);
      setTimeout(onSaved, 50);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const missing = required && !currentUrl;
  return <div style={{ marginTop:10, padding:10, borderRadius:8, border:`1px dashed ${missing ? G.red : tint}66`, background: missing ? G.red+'0a' : 'transparent' }}>
    <div style={{ fontSize:12, fontWeight:700, color: missing ? G.red : tint, marginBottom:8, textTransform:'uppercase', letterSpacing:'.03em' }}>
      📸 {label}{required && !currentUrl && <span> · required</span>}
    </div>
    <input ref={inputRef} type="file" accept="image/*" capture="environment"
      style={{ display:'none' }} onChange={e => upload(e.target.files?.[0])}/>
    {currentUrl ? (
      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
        <a href={currentUrl} target="_blank" rel="noreferrer">
          <img src={currentUrl} alt="" style={{ width:100, height:100, objectFit:'cover', borderRadius:8, border:`1px solid ${G.border}` }}/>
        </a>
        <button onClick={()=>inputRef.current?.click()} disabled={busy}
          style={{ padding:'10px 16px', borderRadius:8, background:G.input, color:G.text,
            border:`1px solid ${G.border}`, fontWeight:700, cursor:'pointer', fontSize:13 }}>
          {busy ? 'Uploading…' : '📷 Replace Photo'}
        </button>
      </div>
    ) : (
      <button onClick={()=>inputRef.current?.click()} disabled={busy}
        style={{ width:'100%', padding:'16px', borderRadius:8, background:tint,
          color:'#fff', border:'none', fontWeight:800, cursor:'pointer', fontSize:15 }}>
        {busy ? 'Uploading…' : '📷 Take / Upload Photo'}
      </button>
    )}
  </div>;
}

function TierTableField({ section, formId, values, setVal }) {
  const tiers = section.tiers || [];
  const rowCount = section.rowCount || 10;
  return <div style={{ overflow:'auto' }}>
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:520 }}>
      <thead><tr>
        <th style={{ textAlign:'left', padding:'6px 8px', color:G.textMuted, fontSize:11 }}>Line Item</th>
        {tiers.map((t, i) => <th key={i} style={{ textAlign:'center', padding:'6px 8px', color:G.textMuted, fontSize:11 }}>{t}</th>)}
      </tr></thead>
      <tbody>
        {Array.from({ length: rowCount }, (_, ri) => (
          <tr key={ri} style={{ borderTop:`1px solid ${G.border}` }}>
            <td style={{ padding:'4px 8px' }}>
              <Input value={values[`${section.id}.line.${ri}.desc`]||''} onChange={e=>setVal(formId, `${section.id}.line.${ri}.desc`, e.target.value)} placeholder={`Item ${ri+1}`} style={{ fontSize:12, padding:'6px 8px' }}/>
            </td>
            {tiers.map((_, ti) => (
              <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[`${section.id}.line.${ri}.${ti}`]||''} onChange={e=>setVal(formId, `${section.id}.line.${ri}.${ti}`, e.target.value)} placeholder="$" style={{ fontSize:12, padding:'6px 8px', textAlign:'right' }}/>
              </td>
            ))}
          </tr>
        ))}
        {(section.addons || []).map((a, ai) => (
          <tr key={a.id} style={{ borderTop:`1px solid ${G.border}`, background:G.input+'80' }}>
            <td style={{ padding:'4px 8px', fontWeight:700 }}>{a.label}</td>
            {tiers.map((_, ti) => (
              <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[`${section.id}.addon.${a.id}.${ti}`]||''} onChange={e=>setVal(formId, `${section.id}.addon.${a.id}.${ti}`, e.target.value)} placeholder="$" style={{ fontSize:12, padding:'6px 8px', textAlign:'right' }}/>
              </td>
            ))}
          </tr>
        ))}
        {(section.totals || []).map((t, i) => (
          <tr key={t.id} style={{ borderTop:i===0?`2px solid ${G.border}`:`1px solid ${G.border}`, background:t.id==='total'?G.green+'11':'transparent' }}>
            <td style={{ padding:'6px 8px', fontWeight:800, textTransform:'uppercase' }}>{t.label}{t.required && ' *'}</td>
            {tiers.map((_, ti) => {
              const key = `${section.id}.total.${t.id}.${ti}`;
              const missing = t.required && !hasVal(values[key]);
              return <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[key]||''} onChange={e=>setVal(formId, key, e.target.value)}
                  placeholder="$"
                  style={{ fontSize:12, padding:'6px 8px', textAlign:'right', fontWeight:700,
                    border:`1px solid ${missing?G.red:G.border}`,
                    color: t.id==='total'?G.green:G.text }}/>
              </td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>;
}

// AI package builder — takes THIS ACCOUNT'S walkthrough/inspection answers
// (whatever call-form the tech is filling) plus THIS ACCOUNT'S pricebook,
// and asks Claude to produce 6 tiered packages using the Service Loop
// Electrical framework (4 Whats + benefit translation + 6 tiers). Every
// line item is walkthrough-cited and pricebook-locked. The top 3 tiers
// auto-fill this form's tier_table section.
function EmPackageBuilder({ jobId, formId, values, setValues, toast }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [expanded, setExpanded] = React.useState(true);

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api(`/api/em-packages/generate/${jobId}`, { method:'POST', body:{ values }});
      if (!r?.ok) { setErr(r?.message || 'AI generation failed'); return; }
      setResult(r);
    } catch (e) {
      setErr(e?.message || 'AI request failed');
    } finally { setBusy(false); }
  };

  const applyMapping = async () => {
    if (!result?.qcaMapping) return;
    setBusy(true);
    try {
      // Client-side apply — setValues (parent) prefixes with section.id and
      // PATCHes the call-form directly. Server-side /apply is redundant here.
      setValues(result.qcaMapping);
      toast && toast('Packages loaded into QCA table.', 'success');
    } finally { setBusy(false); }
  };

  const tierTint = (tier) => ({
    Platinum: '#c8d0dc', Gold: '#e0b84a', Silver: '#a8afba',
    Bronze:   '#b57e4a', Economy: G.textMuted, Basic: G.textMuted,
  }[tier] || G.textMuted);

  return <div style={{ border:`1px solid ${G.blue}55`, background:G.blue+'0a', borderRadius:10, padding:12, marginBottom:14 }}>
    <div onClick={()=>setExpanded(e=>!e)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
      <div>
        <div style={{ fontSize:11, color:G.blue, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em' }}>⚡ AI Package Builder</div>
        <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>Generate 6-tier packages from your walkthrough</div>
      </div>
      <span style={{ fontSize:18, color:G.textMuted }}>{expanded ? '▲' : '▼'}</span>
    </div>
    {expanded && <div style={{ marginTop:10 }}>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:10, lineHeight:1.5 }}>
        Reads Customer / Rapport / History / Area-of-Work / Measurements sections above and returns Platinum → Basic packages with homeowner-benefit language. One-tap "Use these" fills the QCA table's Best / Better / Good columns.
      </div>
      {!result && <Btn v="primary" onClick={generate} disabled={busy} full>{busy ? 'Generating…' : '🤖 Generate Packages'}</Btn>}
      {err && <div style={{ color:G.red, fontSize:12, marginTop:8, padding:'8px 10px', background:G.red+'11', borderRadius:6 }}>{err}</div>}
      {result && <div style={{ marginTop:8 }}>
        {result.opening && <div style={{ fontSize:13, fontStyle:'italic', color:G.text, marginBottom:8, padding:'8px 10px', background:G.input, borderRadius:6 }}>“{result.opening}”</div>}
        {Array.isArray(result.signals) && result.signals.length > 0 && (
          <div style={{ marginBottom:10, padding:'8px 10px', background:G.card, border:`1px solid ${G.border}`, borderRadius:6 }}>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>Signals extracted from your walkthrough ({result.signals.length})</div>
            {result.signals.slice(0, 8).map((s, i) => (
              <div key={i} style={{ fontSize:11, lineHeight:1.5, color:G.text, padding:'3px 0' }}>
                <span style={{ display:'inline-block', minWidth:32, textAlign:'right', color:G.textMuted, fontWeight:700, marginRight:6 }}>{s.weight}</span>
                <span style={{ color:G.text }}>{s.reason}</span>
              </div>
            ))}
          </div>
        )}
        {result.fourWhats && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10, fontSize:12 }}>
          {[
            ['Called for', result.fourWhats.called_for, G.orange],
            ["What's connected", result.fourWhats.connected, G.blue],
            ["What's good", result.fourWhats.good, G.green],
            ["What's MORE", result.fourWhats.more, G.textMuted],
          ].map(([label, txt, tint], i) => txt && <div key={i} style={{ background:tint+'11', borderLeft:`3px solid ${tint}`, padding:'6px 8px', borderRadius:4 }}>
            <div style={{ fontSize:10, fontWeight:700, color:tint, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
            <div style={{ marginTop:2, lineHeight:1.4 }}>{txt}</div>
          </div>)}
        </div>}
        <div style={{ display:'grid', gap:8, marginBottom:10 }}>
          {(result.packages || []).map((p, i) => (
            <div key={i} style={{ border:`1px solid ${tierTint(p.tier)}55`, background:tierTint(p.tier)+'0a', borderRadius:8, padding:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                <div>
                  <div style={{ fontSize:10, color:tierTint(p.tier), fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em' }}>{p.tier}</div>
                  <div style={{ fontSize:14, fontWeight:800, marginTop:2 }}>{p.headline}</div>
                  {p.philosophy && <div style={{ fontSize:12, color:G.textMuted, marginTop:2, fontStyle:'italic' }}>{p.philosophy}</div>}
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:15, fontWeight:800 }}>${(p.totalLow||0).toLocaleString()}–${(p.totalHigh||0).toLocaleString()}</div>
                </div>
              </div>
              {(p.lineItems || []).length > 0 && <div style={{ marginTop:8, borderTop:`1px solid ${G.border}`, paddingTop:6 }}>
                {p.lineItems.map((li, j) => (
                  <div key={j} style={{ display:'flex', justifyContent:'space-between', gap:8, padding:'3px 0', fontSize:12 }}>
                    <div style={{ flex:1 }}>
                      <span style={{ fontWeight:600 }}>{li.title}</span>
                      {li.homeownerBenefit && <span style={{ color:G.textMuted }}> — {li.homeownerBenefit}</span>}
                      {li.needsWalkthrough && <span style={{ color:G.orange, fontSize:10, marginLeft:4 }}>⚠ confirm</span>}
                      {li.inferred && <span style={{ color:G.textMuted, fontSize:10, marginLeft:4 }}>(inferred)</span>}
                    </div>
                    <div style={{ color:G.textMuted, whiteSpace:'nowrap' }}>${(li.priceLow||0).toLocaleString()}–${(li.priceHigh||0).toLocaleString()}</div>
                  </div>
                ))}
              </div>}
              {p.notes && <div style={{ fontSize:11, color:G.textMuted, marginTop:6, fontStyle:'italic' }}>{p.notes}</div>}
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn v="primary" onClick={applyMapping} disabled={busy || !result.qcaMapping} style={{ flex:1 }}>Use top 3 (Platinum / Gold / Silver) → QCA table</Btn>
          <Btn onClick={generate} disabled={busy}>{busy ? '…' : 'Re-generate'}</Btn>
        </div>
      </div>}
    </div>}
  </div>;
}

function DiagnosticChecklistPanel({ jobId, initialState, toast, accountIndustry }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState({});
  // If the account has an industry set, lock the picker to it — techs on an
  // Electric Medic account shouldn't see HVAC / plumbing / roofing options at
  // all. Fallback to generic when no industry is set.
  const lockedType = accountIndustry ? String(accountIndustry).toLowerCase() : null;
  const [serviceType, setServiceType] = useState(lockedType || 'generic');
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try { setState(JSON.parse(initialState || '{}')); } catch { setState({}); }
  }, [initialState]);
  useEffect(() => {
    api(`/api/tech/diagnostic-checklist?serviceType=${serviceType}`).then(r => setItems(r.items || [])).catch(()=>{});
  }, [serviceType]);
  const toggle = async (idx) => {
    const next = { ...state, [idx]: !state[idx] };
    setState(next);
    try { await api(`/api/tech/jobs/${jobId}/checklist-state`, { method:'POST', body:{ state: next }}); }
    catch (e) { /* silent — state is local at minimum */ }
  };
  const done = items.filter((_, i) => state[i]).length;
  return <div style={{ ...styles.card, padding:12, marginBottom:10 }}>
    <div onClick={()=>setCollapsed(c=>!c)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
      <div>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>🩺 Diagnostic Checklist</div>
        <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{done} / {items.length} done</div>
      </div>
      {lockedType ? (
        <span style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{lockedType}</span>
      ) : (
        <select value={serviceType} onChange={e=>{ e.stopPropagation(); setServiceType(e.target.value); }} onClick={e=>e.stopPropagation()} style={{ ...styles.inp, padding:'4px 8px', fontSize:11 }}>
          <option value="generic">Generic</option>
          <option value="hvac">HVAC</option>
          <option value="plumbing">Plumbing</option>
          <option value="electrical">Electrical</option>
          <option value="roofing">Roofing</option>
        </select>
      )}
    </div>
    {!collapsed && <div style={{ marginTop:10 }}>
      {items.map((it, i) => (
        <label key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'6px 0', cursor:'pointer', fontSize:13, lineHeight:1.45, color: state[i] ? G.textMuted : G.text, textDecoration: state[i] ? 'line-through' : 'none' }}>
          <input type="checkbox" checked={!!state[i]} onChange={()=>toggle(i)} style={{ marginTop:3, width:18, height:18, cursor:'pointer' }}/>
          <span>{it}</span>
        </label>
      ))}
    </div>}
  </div>;
}

// Equipment nameplate OCR via Claude Vision. Tech snaps a photo of the
// nameplate, Claude extracts manufacturer / model / SN / install date.
function SerialOCRButton({ onResult, toast }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await api('/api/tech/ocr-serial', { method:'POST', body:{ imageBase64: dataUrl }});
      onResult(r);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return <>
    <Btn sz="md" full onClick={()=>inputRef.current?.click()} disabled={busy}>
      {busy ? <Spinner sz={14}/> : '🔍 Scan SN'}
    </Btn>
    <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }} onChange={e=>handle(e.target.files?.[0])}/>
  </>;
}

// Daily summary card — top of the Today view. Gives the tech a quick read
// on how loaded the day is + a one-tap "running late" broadcast button.
function DailySummaryCard({ summary, onRunningLate }) {
  const s = summary || {};
  return <div style={{ padding:'14px 18px 0', display:'flex', flexDirection:'column', gap:8 }}>
    <div style={{ ...styles.card, padding:14, background:G.input, borderColor:G.cardBorder }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Today at a glance</div>
        <Btn sz="xs" v="ghost" onClick={onRunningLate}>⏰ Running Late</Btn>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
        <Stat label="Jobs"      value={s.totalJobsToday || 0}/>
        <Stat label="Remaining" value={s.remainingJobsToday || 0} color={G.cyan}/>
        <Stat label="Hours"     value={`${s.totalHoursToday || 0}h`}/>
        <Stat label="Potential" value={`$${Math.round((s.potentialToday||0))}`} color={G.green}/>
      </div>
      {s.completedJobsToday > 0 && <div style={{ fontSize:11, color:G.textMuted, marginTop:8, textAlign:'center' }}>
        ✓ {s.completedJobsToday} done · ${Math.round(s.collectedToday)} collected
      </div>}
    </div>
  </div>;
}
function Stat({ label, value, color }) {
  return <div style={{ textAlign:'center' }}>
    <div style={{ fontSize:18, fontWeight:800, color: color || G.text }}>{value}</div>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</div>
  </div>;
}

// Tomorrow preview — small card at the bottom of Today view so the tech
// can mentally prep for the next day without leaving the home screen.
function TomorrowPreview() {
  const [data, setData] = useState(null);
  useEffect(() => { api('/api/tech/me/tomorrow').then(setData).catch(()=>{}); }, []);
  if (!data || data.count === 0) return null;
  return <div style={{ padding:'14px 18px 18px' }}>
    <div style={{ ...styles.card, padding:14, background:G.purpleDim+'22', borderColor:G.purple+'44' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <strong style={{ fontSize:13 }}>📅 Tomorrow</strong>
        <Badge c={G.purple} bg={G.purpleDim}>{data.count} stop{data.count===1?'':'s'}</Badge>
      </div>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:8 }}>
        Starts at {data.firstStartTime || '(no time)'} · ${Math.round(data.totalEstimated||0)} estimated
      </div>
      {(data.jobs||[]).slice(0,3).map((j,i)=>(
        <div key={i} style={{ fontSize:12, padding:'4px 0', color:G.text, borderTop: i===0?'none':`1px solid ${G.border}` }}>
          {j.startTime||''} · {j.contactFirstName||'(?)'}{j.name?` — ${j.name}`:''}
        </div>
      ))}
      {data.jobs.length > 3 && <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>+ {data.jobs.length - 3} more</div>}
    </div>
  </div>;
}

// Running Late modal — broadcasts a "running ~N min late" SMS to every
// remaining customer in this tech's schedule today. One tap, multiple texts.
function RunningLateModal({ onClose, toast }) {
  const [delay, setDelay] = useState(30);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api('/api/tech/running-late', { method:'POST', body:{ delayMinutes: delay }});
      toast(`✓ Sent to ${r.sent} customer${r.sent===1?'':'s'}${r.failed?` (${r.failed} failed)`:''}`);
      onClose();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={420}>
    <h3 style={{ margin:'0 0 6px' }}>Running Late?</h3>
    <p style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      We'll text every remaining customer on today's schedule that you're running about <strong>{delay} minutes</strong> behind.
    </p>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:6, marginBottom:14 }}>
      {[15, 30, 45, 60].map(m => (
        <button key={m} onClick={()=>setDelay(m)} style={{
          padding:'10px 6px', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer',
          border:`1px solid ${delay===m ? G.orange : G.cardBorder}`,
          background: delay===m ? G.orange : 'transparent',
          color: delay===m ? '#fff' : G.text,
        }}>{m} min</button>
      ))}
    </div>
    <div style={{ display:'flex', gap:8 }}>
      <Btn v="ghost" onClick={onClose} disabled={busy} style={{ flex:1 }}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy} style={{ flex:2, background:G.orange, borderColor:G.orange }}>
        {busy ? <Spinner sz={14}/> : `Text everyone (${delay} min late)`}
      </Btn>
    </div>
  </Modal>;
}

// SOS modal — three options: emergency call (911), ring dispatcher, text
// dispatcher. Logged server-side + texts the office automatically.
function SOSModal({ onClose, toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fire = async (reason) => {
    setBusy(true);
    try {
      const r = await api('/api/tech/sos', { method:'POST', body:{ reason }});
      setData(r);
      if (r.smsSent) toast('🆘 Office notified');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={420}>
    <div style={{ textAlign:'center', marginBottom:14 }}>
      <div style={{ fontSize:42 }}>🆘</div>
      <h3 style={{ margin:'8px 0 4px', color:G.red }}>Need help right now?</h3>
      <p style={{ fontSize:13, color:G.textMuted }}>Pick the fastest option — we'll alert the office automatically.</p>
    </div>
    <a href="tel:911" style={{ display:'block', padding:14, marginBottom:8, borderRadius:10, background:G.red, color:'#fff', textAlign:'center', textDecoration:'none', fontWeight:800, fontSize:16 }}>🚨 Call 911 — Emergency</a>
    {data?.dispatcher?.phone && <>
      <a href={`tel:${data.dispatcher.phone}`} style={{ display:'block', padding:14, marginBottom:8, borderRadius:10, background:G.orange, color:'#fff', textAlign:'center', textDecoration:'none', fontWeight:700, fontSize:15 }}>📞 Call dispatcher ({data.dispatcher.name})</a>
    </>}
    <Btn v="primary" full onClick={()=>fire('safety')} disabled={busy} style={{ marginBottom:8 }}>
      {busy ? <Spinner sz={14}/> : (data?.smsSent ? '✓ Office notified' : '🆘 Alert office (SMS)')}
    </Btn>
    <Btn v="ghost" full onClick={()=>fire('tech-help')} disabled={busy} style={{ marginBottom:14 }}>
      💬 I need a tech to assist me on this job
    </Btn>
    <Btn v="ghost" full onClick={onClose}>Cancel</Btn>
  </Modal>;
}

// AI-generated upsell tips. Calls Claude with the customer's equipment + job
// context, returns 2-3 short headlines + talking points the tech can use.
function UpsellTipsButton({ jobId, toast }) {
  const [tips, setTips] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const fetch = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/tech/jobs/${jobId}/upsell-tips`, { method:'POST', body:{}});
      setTips(r.tips || []);
      setOpen(true);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <>
    <Btn sz="sm" v="ghost" onClick={fetch} disabled={busy} full>
      {busy ? <Spinner sz={14}/> : '🤖 Upsell Tips'}
    </Btn>
    {open && <Modal open onClose={()=>setOpen(false)} width={480}>
      <h3 style={{ margin:'0 0 6px' }}>🤖 Upsell Talking Points</h3>
      <p style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
        Based on this customer's equipment + history. Use as a starting point — don't push anything they don't need.
      </p>
      {tips?.length === 0 && <div style={{ padding:14, color:G.textMuted, textAlign:'center' }}>No suggestions for this job — looks clean.</div>}
      {tips?.map((t, i) => <div key={i} style={{ ...styles.card, padding:14, marginBottom:10, background:G.purpleDim+'22', borderColor:G.purple+'44' }}>
        <div style={{ fontSize:14, fontWeight:800, color:G.purple, marginBottom:6 }}>{t.headline}</div>
        <div style={{ fontSize:13, lineHeight:1.5, color:G.text }}>{t.talk}</div>
      </div>)}
      <Btn v="primary" full onClick={()=>setOpen(false)}>Got it</Btn>
    </Modal>}
  </>;
}

// Customer card on the JobDetail with inline edit. Techs in the field
// frequently get a corrected phone/email at the door — they should be able
// to fix it without calling the office. Saves via the existing tech endpoint.
function TechCustomerCard({ contact, onSmsOpen, onSaved, toast }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ phone: contact.phone || '', email: contact.email || '', address: contact.address || '' });
  const [busy, setBusy] = useState(false);
  // Reset form when the underlying contact changes (e.g. after save refresh).
  useEffect(() => { setForm({ phone: contact.phone || '', email: contact.email || '', address: contact.address || '' }); }, [contact.id, contact.phone, contact.email, contact.address]);
  const save = async () => {
    setBusy(true);
    try {
      await api(`/api/tech/contacts/${contact.id}`, { method:'PATCH', body: form });
      toast?.('✓ Saved');
      setEditing(false);
      onSaved && onSaved();
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };
  if (editing) {
    return <div style={{ ...styles.card, padding:14, background:G.input, marginBottom:10 }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>
        ✏️ Edit customer info
      </div>
      <div style={{ display:'grid', gap:8 }}>
        <Input type="tel" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="Phone" autoFocus/>
        <Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="Email"/>
        <Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} placeholder="Address"/>
      </div>
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <Btn sz="sm" v="ghost" onClick={()=>setEditing(false)} style={{ flex:1 }} disabled={busy}>Cancel</Btn>
        <Btn sz="sm" v="primary" onClick={save} style={{ flex:1 }} disabled={busy}>{busy ? <Spinner sz={12}/> : 'Save'}</Btn>
      </div>
    </div>;
  }
  return <div style={{ ...styles.card, padding:14, background:G.input, marginBottom:10 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
      <strong>{contact.firstName} {contact.lastName}</strong>
      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        <button onClick={()=>setEditing(true)} aria-label="Edit customer" style={{ background:'transparent', border:'none', color:G.textMuted, cursor:'pointer', fontSize:14, padding:4 }}>✏️</button>
        {contact.phone && <a href={`tel:${contact.phone}`} style={{ color:G.green, textDecoration:'none', fontWeight:700 }}>📞 Call</a>}
      </div>
    </div>
    {contact.address && <div style={{ fontSize:13, color:G.textMuted }}>{contact.address}</div>}
    {contact.email && <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>✉ {contact.email}</div>}
    {contact.phone && <div style={{ display:'flex', gap:8, marginTop:8 }}>
      <button onClick={onSmsOpen} style={{ flex:1, textAlign:'center', padding:'8px', background:G.cyanLight, color:G.cyan, borderRadius:6, fontSize:12, fontWeight:700, border:'none', cursor:'pointer' }}>💬 Text</button>
      <a href={`https://maps.apple.com/?q=${encodeURIComponent(contact.address||'')}`} target="_blank" rel="noreferrer" style={{ flex:1, textAlign:'center', padding:'8px', background:G.purpleDim, color:G.purple, borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>🗺️ Navigate</a>
    </div>}
  </div>;
}

function TechLeaderboard({ toast }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  useEffect(() => {
    api(`/api/tech/leaderboard?period=${period}`).then(setData).catch(e=>toast(e.message,'error'));
  }, [period]);
  if (!data) return <div style={{ padding:24 }}><Spinner sz={28}/></div>;
  const board = data.leaderboard || [];
  return <div style={{ padding:16, paddingBottom:100 }}>
    <h2 style={{ margin:'0 0 12px', fontSize:18 }}>🏆 Leaderboard</h2>
    <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      {['week','month','ytd'].map(p => (
        <Btn key={p} sz="xs" v={period===p?'primary':'ghost'} onClick={()=>setPeriod(p)}>
          {p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'YTD'}
        </Btn>
      ))}
    </div>
    {board.length === 0 ? <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted }}>No completed jobs this {period} yet — get out there.</div>
      : board.map((t, i) => (
        <div key={t.techId} style={{ ...styles.card, padding:12, marginBottom:8,
          background: t.isMe ? G.purpleDim+'22' : G.card,
          border: t.isMe ? `2px solid ${G.purple}` : `1px solid ${G.cardBorder}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <div style={{ width:32, textAlign:'center', fontSize:18, fontWeight:800,
              color: i===0?'#fbbf24': i===1?'#94a3b8': i===2?'#fb923c': G.textMuted }}>
              {i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${t.rank}`}
            </div>
            <Av name={t.name}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700 }}>{t.name}{t.isMe && <span style={{ marginLeft:6, fontSize:10, color:G.purple, fontWeight:800 }}>YOU</span>}</div>
              <div style={{ height:5, background:G.input, borderRadius:3, marginTop:4 }}>
                <div style={{ width:`${t.revenuePctOfTop}%`, height:'100%', background:`linear-gradient(90deg, ${G.purple}, ${G.cyan})`, borderRadius:3 }}/>
              </div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:6, fontSize:12, color:G.textMuted, marginTop:8 }}>
            <div><strong style={{ color:G.text }}>{t.jobsCompleted}</strong> jobs</div>
            <div>💵 <strong style={{ color:G.green }}>${Number(t.revenue||0).toFixed(0)}</strong></div>
            {t.onTimePct !== null && <div>⏰ <strong style={{ color: t.onTimePct>=95?G.green:t.onTimePct>=80?G.yellow:G.red }}>{t.onTimePct}%</strong></div>}
            {t.fiveStarReviews > 0 && <div>⭐ <strong style={{ color:G.text }}>{t.fiveStarReviews}</strong> 5-star{t.fiveStarReviewsGoogle > 0 && <span style={{ color:G.cyan, marginLeft:4 }}>(🌟 {t.fiveStarReviewsGoogle} Google)</span>}</div>}
            {t.callbacks > 0 && <div>🔁 <strong style={{ color:G.red }}>{t.callbacks}</strong> callbacks</div>}
            {t.hoursSaved > 0 && <div>⚡ <strong style={{ color:G.green }}>{t.hoursSaved}h</strong> saved</div>}
            {t.upsellUpliftCents > 0 && <div>📈 <strong style={{ color:G.green }}>${Math.round(t.upsellUpliftCents/100)}</strong> upsell</div>}
          </div>
          {t.badges?.length > 0 && <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
            {t.badges.map(b => <Badge key={b} c={G.purple} bg={G.purpleDim}>{b}</Badge>)}
          </div>}
        </div>
      ))}
  </div>;
}

// Read-only customer search for techs in the field — typing 2+ chars hits the API.
function CustomerSearch({ toast }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.length < 2) { setRows([]); return; }
    const t = setTimeout(() => {
      setBusy(true);
      api(`/api/tech/contacts/search?q=${encodeURIComponent(q)}`).then(setRows).catch(()=>{}).finally(()=>setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return <div style={{ padding:'14px 18px' }}>
    <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name, phone, address…"
      autoFocus style={{ width:'100%', padding:'14px 16px', borderRadius:10, border:`1px solid ${G.cardBorder}`,
      background:G.input, color:G.text, fontSize:16, outline:'none', marginBottom:14 }}/>
    {busy && <div style={{ textAlign:'center', padding:20 }}><Spinner sz={18}/></div>}
    {!busy && q.length >= 2 && rows.length === 0 && (
      <div style={{ textAlign:'center', color:G.textMuted, fontSize:13, padding:20 }}>No matches.</div>
    )}
    {rows.map(c => (
      <div key={c.id} style={{ ...styles.card, padding:14, marginBottom:8 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
          <strong style={{ fontSize:14 }}>{c.firstName} {c.lastName}</strong>
          {c.stage && <Badge c={G.cyan} bg={G.cyanLight}>{c.stage}</Badge>}
        </div>
        <div style={{ fontSize:12, color:G.textMuted }}>{c.phone}</div>
        <div style={{ fontSize:12, color:G.textMuted }}>{c.address}{c.city ? `, ${c.city}` : ''}</div>
        <div style={{ display:'flex', gap:8, marginTop:10 }}>
          {c.phone && <a href={`tel:${c.phone}`} style={{ flex:1, textAlign:'center', padding:'10px', background:G.greenLight, color:G.green, borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>📞 Call</a>}
          {c.phone && <a href={`sms:${c.phone}`} style={{ flex:1, textAlign:'center', padding:'10px', background:G.cyanLight, color:G.cyan, borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>💬 Text</a>}
          {c.address && <a href={`https://maps.apple.com/?q=${encodeURIComponent(c.address)}`} target="_blank" rel="noreferrer" style={{ flex:1, textAlign:'center', padding:'10px', background:G.purpleDim, color:G.purple, borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>🗺️ Map</a>}
        </div>
      </div>
    ))}
  </div>;
}

function PrevanModal({ open, data, onClose }) {
  if (!open || !data) return null;
  return <Modal open onClose={onClose} width={620}>
    <h2 style={{ margin:'0 0 4px 0' }}>📋 Pre-Van Checklist</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>{data.isTomorrow ? "Tomorrow's" : "Today's"} route — {data.jobs.length} job{data.jobs.length>1?'s':''} on {data.date}</div>

    {data.consolidatedPartsList.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: G.green+'66' }}>
      <strong style={{ color:G.green, fontSize:14 }}>📦 Load on the truck</strong>
      <div style={{ marginTop:8 }}>
        {data.consolidatedPartsList.map((p,i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
            <span>{p.name}</span>
            <strong>{p.totalQty} {p.unit}</strong>
          </div>
        ))}
      </div>
    </div>}

    {data.jobs.map(j => (
      <div key={j.id} style={{ ...styles.card, padding:14, marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
          <strong>{j.startTime||''} · {j.name}</strong>
          {j.estimate && <Badge c={G.cyan} bg={G.cyanLight}>📄 {j.estimate.num}</Badge>}
        </div>
        {j.contact && <div style={{ fontSize:13, color:G.textMuted }}>{j.contact.firstName} {j.contact.lastName} · {j.contact.phone}</div>}
        <div style={{ fontSize:12, color:G.textMuted }}>📍 {j.address||'—'}</div>
        {j.contact?.notes && <div style={{ fontSize:12, color:G.yellow, marginTop:4 }}>🏠 {j.contact.notes}</div>}
        {j.jobNotes && <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>📝 {j.jobNotes}</div>}
        {j.partsToBring?.length > 0 && <div style={{ marginTop:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>Parts for this job</div>
          {j.partsToBring.map((p,i) => <div key={i} style={{ fontSize:13, padding:'2px 0' }}>• {p.qty} {p.unit} — {p.name}</div>)}
        </div>}
        {j.onSiteEquipment?.length > 0 && <div style={{ marginTop:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>On-site equipment</div>
          {j.onSiteEquipment.map((e,i) => <div key={i} style={{ fontSize:12, color:G.textMuted, padding:'1px 0' }}>⚙️ {e.makeModel || e.type}{e.serial?` · SN ${e.serial}`:''}</div>)}
        </div>}
      </div>
    ))}

    <div style={{ ...styles.card, padding:14, marginTop:10, background:G.input }}>
      <strong style={{ fontSize:13 }}>✅ Truck Checklist</strong>
      {(data.truckChecklist||[]).map((c,i) => <div key={i} style={{ fontSize:13, padding:'4px 0' }}>☐ {c}</div>)}
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}><Btn onClick={onClose}>Close</Btn></div>
  </Modal>;
}

function Section({ label, children }) {
  return <div style={{ padding:'4px 18px 14px 18px' }}>
    <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em', margin:'10px 4px' }}>{label}</div>
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>{children}</div>
  </div>;
}

// Infer the work kind (inspection / installation / service / maintenance / warranty /
// estimate) from job.kind if explicitly set, otherwise heuristically from job.name.
// Techs glance at this before tapping in to know what they're walking into.
const KIND_STYLES = {
  inspection:   { icon:'🔍', label:'Inspection',   color:'#1d4ed8' },
  installation: { icon:'🔧', label:'Installation', color:'#15803d' },
  service:      { icon:'🛠️', label:'Service Call', color:'#c2410c' },
  maintenance:  { icon:'🧰', label:'Maintenance',  color:'#0e7490' },
  warranty:     { icon:'🛡️', label:'Warranty',     color:'#7e22ce' },
  estimate:     { icon:'📐', label:'Estimate',     color:'#a16207' },
};
function getJobKind(j) {
  if (j?.kind && KIND_STYLES[j.kind]) return j.kind;
  const t = String(j?.name || '').toLowerCase();
  if (/\binspect|inspection|inspect ?ion\b/.test(t)) return 'inspection';
  if (/\binstall|installation|new (system|unit|panel|service)|replacement\b/.test(t)) return 'installation';
  if (/\bmaintenan|tune[- ]?up|seasonal|pm\b/.test(t)) return 'maintenance';
  if (/\bwarranty|callback|recall\b/.test(t)) return 'warranty';
  if (/\bestimate|quote|bid|walk[- ]?through\b/.test(t)) return 'estimate';
  return 'service';
}

function JobCard({ j, onOpen, highlight }) {
  const kind = getJobKind(j);
  const ks = KIND_STYLES[kind];
  return <div onClick={onOpen} style={{ ...styles.card, padding:14, cursor:'pointer', borderColor: highlight ? STATUS_COLOR[j.status]+'88' : G.cardBorder }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6, gap:6, flexWrap:'wrap' }}>
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <Badge c={STATUS_COLOR[j.status]} bg={STATUS_COLOR[j.status]+'22'}>{j.status}</Badge>
        <Badge c={ks.color} bg={ks.color+'22'}>{ks.icon} {ks.label}</Badge>
      </div>
      <div style={{ fontSize:12, color:G.textMuted, fontWeight:600 }}>{j.startTime || ''}</div>
    </div>
    <div style={{ fontWeight:700, fontSize:15 }}>{j.name}</div>
    {j.contact && <div style={{ fontSize:13, color:G.textMuted, marginTop:2 }}>{j.contact.firstName} {j.contact.lastName}</div>}
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>📍 {j.address || j.contact?.address || '—'}</div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
      <span style={{ fontSize:13, color:G.green, fontWeight:700 }}>{fmtUsd(j.est||0)}</span>
      {NEXT[j.status] && <div style={{ fontSize:12, color:G.purple, fontWeight:700 }}>Tap → {NEXT[j.status]} →</div>}
    </div>
  </div>;
}

function JobDrawer({ job, onClose, onChanged, toast, accountIndustry }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const sendSms = async () => {
    if (!smsBody.trim()) return;
    setSmsBusy(true);
    try {
      await api('/api/tech/sms', { method:'POST', body:{ jobId: job.id, body: smsBody.trim() }});
      toast('💬 Sent');
      setSmsBody(''); setSmsOpen(false);
    } catch(e) { toast(e.message,'error'); }
    finally { setSmsBusy(false); }
  };
  const SMS_QUICK = [
    '👋 Hi, this is your tech — on the way now.',
    '🕒 Running about 15 minutes late, sorry for the delay.',
    '✅ Wrapping up here, on my way to your stop next.',
    '📋 Heading out — invoice + summary coming to your email shortly.',
  ];
  const [payOpen, setPayOpen] = useState(false);
  const [enrouteOpen, setEnrouteOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [completeWarnOpen, setCompleteWarnOpen] = useState(false);
  const scheduleFollowup = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/tech/jobs/${job.id}/schedule-followup`, { method:'POST', body:{ monthsOut: 6 }});
      toast(`✓ 6-month tune-up reminder set for ${r.dueDate}`);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!job) { setDetail(null); return; }
    setLoading(true);
    api(`/api/tech/jobs/${job.id}`).then(setDetail).catch(()=>{}).finally(()=>setLoading(false));
  }, [job]);

  if (!job) return null;
  const next = NEXT[detail?.status || job.status];

  const advance = async (newStatus, opts = {}) => {
    // Photo-gate the Completed transition. Refusing-without-photos opens a
    // confirm dialog; the tech can choose to proceed anyway (logged) or
    // bounce out to add a photo. Important for warranty/dispute defense.
    if (newStatus === 'Completed' && !opts.skipPhotoCheck) {
      const hasPhotos = (detail?.photos?.length || 0) > 0;
      if (!hasPhotos) { setCompleteWarnOpen(true); return; }
    }
    setBusy(true);
    try {
      const body = { status: newStatus };
      await api(`/api/tech/jobs/${job.id}/status`, { method:'POST', body });
      const updated = await api(`/api/tech/jobs/${job.id}`); setDetail(updated);
      onChanged();
      toast(`Job → ${newStatus}`);
      // Auto-pop the mandatory service-call form the moment the tech marks
      // themselves on site. Non-blocking — they can close and reopen from the
      // panel — but it forces the workflow to start before they get distracted.
      if (newStatus === 'On Site') {
        try {
          const r = await api(`/api/call-forms/for-job/${job.id}`);
          const hasIncomplete = (r.forms || []).some(f => !f.response?.isComplete);
          if (hasIncomplete) setFormModalOpen(true);
        } catch {}
      }
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api(`/api/tech/jobs/${job.id}/note`, { method:'POST', body:{ body: noteText.trim() }});
      setNoteText('');
      const updated = await api(`/api/tech/jobs/${job.id}`); setDetail(updated);
      toast('Note saved');
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  // Called by PhotoPicker after upload completes — already gives us {url, name, mime, size}.
  const attachPhoto = async (uploaded) => {
    if (!uploaded) return;
    setBusy(true);
    try {
      await api(`/api/tech/jobs/${job.id}/photo`, { method:'POST', body:{ url: uploaded.url, stage: detail?.status||'in_progress' } });
      const updated = await api(`/api/tech/jobs/${job.id}`); setDetail(updated);
      toast('Photo uploaded');
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const _kind = getJobKind(detail || job);
  const _ks = KIND_STYLES[_kind];
  return <Modal open onClose={onClose} width={520}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, gap:8, flexWrap:'wrap' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
        <h2 style={{ margin:0, fontSize:18 }}>{job.name}</h2>
        <Badge c={_ks.color} bg={_ks.color+'22'}>{_ks.icon} {_ks.label}</Badge>
      </div>
      <Badge c={STATUS_COLOR[detail?.status||job.status]} bg={STATUS_COLOR[detail?.status||job.status]+'22'}>{detail?.status||job.status}</Badge>
    </div>

    {detail?.contact && <TechCustomerCard
      contact={detail.contact}
      onSmsOpen={()=>setSmsOpen(true)}
      onSaved={async () => { const u = await api(`/api/tech/jobs/${job.id}`).catch(()=>null); if (u) setDetail(u); onChanged && onChanged(); }}
      toast={toast}
    />}

    {detail?.notes && <div style={{ ...styles.card, padding:12, marginBottom:10, fontSize:13, lineHeight:1.5 }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>📝 Job Notes</div>
      {detail.notes}
    </div>}

    {detail?.equipment?.length > 0 && <div style={{ ...styles.card, padding:12, marginBottom:10 }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>⚙️ Equipment On Site</div>
      {detail.equipment.map(e => <div key={e.id} style={{ fontSize:13, padding:'4px 0' }}>
        <strong>{e.manufacturer} {e.model}</strong> · {e.type}
        {e.serialNumber && <span style={{ color:G.textMuted }}> · SN {e.serialNumber}</span>}
      </div>)}
    </div>}

    {/* MANDATORY service-call forms (EM Electrical Service Call Questions
        etc). Blocks the Advance → Completed transition until fully filled. */}
    {(detail?.status || job.status) !== 'Cancelled' && (
      <ServiceCallFormsPanel jobId={job.id} toast={toast}/>
    )}

    {/* Diagnostic checklist — pulled from account or industry default. State
        persists per job so the tech can come back to it later. */}
    {['On Site','Dispatched','En Route'].includes(detail?.status || job.status) && (
      <DiagnosticChecklistPanel jobId={job.id} initialState={detail?.diagnosticChecklistState} toast={toast} accountIndustry={accountIndustry}/>
    )}

    {detail?.photos?.length > 0 && <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6, padding:'0 4px' }}>📸 Photos</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
        {detail.photos.map(p => <a key={p.id} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="" style={{ width:'100%', aspectRatio:'1', objectFit:'cover', borderRadius:6 }}/></a>)}
      </div>
    </div>}

    {/* Notes — visible + add inline */}
    <div style={{ ...styles.card, padding:12, marginBottom:10 }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>📝 Notes</div>
      {detail?.notesList?.map(n => (
        <div key={n.id} style={{ fontSize:13, padding:'6px 0', borderBottom:`1px solid ${G.border}` }}>
          <div>{n.body}</div>
          <div style={{ fontSize:10, color:G.textMuted, marginTop:2 }}>{n.authorName} · {new Date(n.createdAt).toLocaleString([], { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}</div>
        </div>
      ))}
      {(!detail?.notesList || detail.notesList.length === 0) && <div style={{ fontSize:12, color:G.textMuted, padding:'4px 0' }}>No notes yet.</div>}
      <div style={{ display:'flex', gap:6, marginTop:8, alignItems:'flex-start' }}>
        <Textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note (or tap 🎤 to dictate)…" style={{ flex:1, minHeight:46, fontSize:13 }}/>
        <VoiceDictateButton onAppend={(txt) => setNoteText(t => t ? `${t} ${txt}` : txt)} toast={toast}/>
        <Btn sz="sm" v="primary" onClick={addNote} disabled={busy || !noteText.trim()}>Save</Btn>
      </div>
    </div>

    <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
      {/* Dedicated En Route button — replaces the generic Advance when the
          next state is En Route. Opens a proper picker so the tech can set
          ETA and preview the SMS before firing it. */}
      {next === 'En Route' && <Btn v="primary" onClick={()=>setEnrouteOpen(true)} disabled={busy} full style={{ flex:'1 1 100%', background:G.cyan, borderColor:G.cyan }}>
        🚐 On My Way → Notify customer
      </Btn>}
      {next && next !== 'En Route' && <Btn v="primary" onClick={()=>advance(next)} disabled={busy} full style={{ flex:'1 1 100%' }}>{busy?<Spinner/>:`Advance → ${next}`}</Btn>}
      {/* On-site / diagnosis aid — AI upsell tips */}
      {['On Site','Dispatched','En Route'].includes(detail?.status || job.status) && (
        <span style={{ flex:'1 1 100%' }}><UpsellTipsButton jobId={job.id} toast={toast}/></span>
      )}
      {/* Post-completion actions — review + follow-up + email + payment */}
      {(detail?.status || job.status) === 'Completed' && <>
        <Btn v="primary" onClick={()=>setReviewOpen(true)} disabled={busy} style={{ flex:'1 1 100%', background:G.yellow, borderColor:G.yellow, color:'#000' }}>⭐ Capture Review</Btn>
        <Btn onClick={scheduleFollowup} disabled={busy} style={{ flex:'1 1 100%' }}>📅 Schedule 6-month tune-up</Btn>
      </>}
      <span style={{ flex:'1', display:'flex' }}><PhotoPicker label="📸 Photo" upload toast={toast} onPicked={attachPhoto}/></span>
      <span style={{ flex:'1', display:'flex' }}><SerialOCRButton onResult={(r) => toast(`📷 ${r.manufacturer||'?'} ${r.model||''} · SN: ${r.serialNumber||'?'}`)} toast={toast}/></span>
      <Btn v="primary" onClick={()=>setPayOpen(true)} disabled={busy} style={{ flex:'1', background:G.green, borderColor:G.green }}>💳 Collect Payment</Btn>
      {detail?.contact?.email && <Btn onClick={()=>setEmailOpen(true)} disabled={busy} style={{ flex:'1' }}>✉ Email</Btn>}
      <Btn onClick={onClose} style={{ flex:'1' }}>Close</Btn>
    </div>

    {payOpen && <CollectPaymentModal job={job} onClose={()=>setPayOpen(false)} onDone={(r) => { setPayOpen(false); onChanged(); toast(`✓ Payment link sent · $${r.total.toFixed(2)}`); }} toast={toast}/>}
    {smsOpen && <Modal open onClose={()=>setSmsOpen(false)} width={420}>
      <h3 style={{ margin:'0 0 4px' }}>Text {detail?.contact?.firstName || 'customer'}</h3>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>Sent through the business number — the office sees this conversation too.</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
        {SMS_QUICK.map(s => <Btn key={s} sz="xs" v="ghost" onClick={()=>setSmsBody(s)}>{s.slice(0, 28)}…</Btn>)}
      </div>
      <Textarea value={smsBody} onChange={e=>setSmsBody(e.target.value)} placeholder="Type your message…" style={{ minHeight:90 }}/>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
        <span style={{ fontSize:11, color:G.textMuted }}>{smsBody.length} chars</span>
        <div style={{ display:'flex', gap:8 }}>
          <Btn v="ghost" onClick={()=>setSmsOpen(false)}>Cancel</Btn>
          <Btn v="primary" onClick={sendSms} disabled={smsBusy || !smsBody.trim()}>{smsBusy?<Spinner sz={14}/>:'Send'}</Btn>
        </div>
      </div>
    </Modal>}

    <div style={{ marginTop:10, padding:10, background:G.input, borderRadius:8, fontSize:11, color:G.textMuted, lineHeight:1.5 }}>
      💡 Advancing → <strong style={{color:G.text}}>On Site</strong> auto-creates a pre-job inspection form.
      → <strong style={{color:G.text}}>Completed</strong> creates a post-job sign-off + texts the customer their thank-you.
    </div>
    {enrouteOpen && <EnrouteModal job={job} contact={detail?.contact} onClose={()=>setEnrouteOpen(false)} onDone={async () => {
      setEnrouteOpen(false);
      const u = await api(`/api/tech/jobs/${job.id}`).catch(()=>null); if (u) setDetail(u);
      onChanged(); toast('🚐 Customer notified — drive safe.');
    }} toast={toast}/>}
    {reviewOpen && <ReviewModal job={job} contact={detail?.contact} onClose={()=>setReviewOpen(false)} toast={toast}/>}
    {emailOpen && <EmailModal job={job} contact={detail?.contact} onClose={()=>setEmailOpen(false)} toast={toast}/>}
    {completeWarnOpen && <Modal open onClose={()=>setCompleteWarnOpen(false)} width={420}>
      <h3 style={{ margin:'0 0 6px' }}>📸 No photos on this job</h3>
      <p style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
        Photos protect you (and the business) on warranty + dispute claims. Take a quick "before" and "after" shot — it takes 10 seconds.
      </p>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={()=>{ setCompleteWarnOpen(false); }} style={{ flex:1 }}>Add a photo</Btn>
        <Btn v="primary" onClick={async ()=>{ setCompleteWarnOpen(false); await advance('Completed', { skipPhotoCheck:true }); }} style={{ flex:1, background:G.red, borderColor:G.red }}>Complete anyway</Btn>
      </div>
    </Modal>}

    {/* Auto-pop on 'On Site' transition — full-screen modal with the mandatory
        service call form. Techs can dismiss but the panel remains rendered
        below in the job detail; Complete is still gated on the form being
        100% filled. */}
    {formModalOpen && <Modal open onClose={()=>setFormModalOpen(false)} width={720}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, gap:8 }}>
        <div>
          <div style={{ fontSize:11, color:G.red, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em' }}>⚠ Fill before leaving site</div>
          <div style={{ fontSize:18, fontWeight:800 }}>Service Call Form</div>
        </div>
        <Btn sz="sm" onClick={()=>setFormModalOpen(false)}>Close</Btn>
      </div>
      <div style={{ maxHeight:'75vh', overflow:'auto', paddingRight:4 }}>
        <ServiceCallFormsPanel jobId={job.id} toast={toast}/>
      </div>
    </Modal>}
  </Modal>;
}

// Dedicated "On My Way" modal with ETA picker + SMS preview.
function EnrouteModal({ job, contact, onClose, onDone, toast }) {
  const [eta, setEta] = useState(20);
  const [customMessage, setCustomMessage] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    setBusy(true);
    try {
      await api(`/api/tech/jobs/${job.id}/enroute`, { method:'POST', body:{ etaMinutes: eta, customMessage: showCustom ? customMessage : null }});
      onDone();
    } catch(e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={460}>
    <h3 style={{ margin:'0 0 6px' }}>🚐 On My Way — {contact?.firstName || 'Customer'}</h3>
    <p style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Pick your ETA — we'll text the customer and flip this job to En Route.</p>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:6, marginBottom:14 }}>
      {[10, 15, 20, 30, 45].map(m => (
        <button key={m} onClick={()=>setEta(m)} style={{
          padding:'10px 6px', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer',
          border:`1px solid ${eta===m ? G.cyan : G.cardBorder}`,
          background: eta===m ? G.cyan : 'transparent',
          color: eta===m ? '#fff' : G.text,
        }}>{m} min</button>
      ))}
    </div>
    <div style={{ ...styles.card, padding:12, marginBottom:14, background:G.input, fontSize:12 }}>
      <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', marginBottom:4 }}>SMS Preview</div>
      <em style={{ color:G.text }}>
        {showCustom && customMessage
          ? customMessage
          : `Hi ${contact?.firstName || 'there'}, this is your tech from the office. I'm on my way — ETA about ${eta} minutes. Reply STOP to opt out.`}
      </em>
    </div>
    <button onClick={()=>setShowCustom(s=>!s)} style={{ background:'transparent', border:'none', color:G.cyan, fontSize:12, cursor:'pointer', marginBottom:10 }}>
      {showCustom ? '↳ Use default message' : '✎ Customize message'}
    </button>
    {showCustom && <Textarea value={customMessage} onChange={e=>setCustomMessage(e.target.value)} placeholder="Custom message…" style={{ minHeight:60, marginBottom:10, fontSize:13 }}/>}
    <div style={{ display:'flex', gap:8 }}>
      <Btn v="ghost" onClick={onClose} disabled={busy} style={{ flex:1 }}>Cancel</Btn>
      <Btn v="primary" onClick={fire} disabled={busy} style={{ flex:2, background:G.cyan, borderColor:G.cyan }}>
        {busy ? <Spinner sz={14}/> : 'Send & Mark En Route'}
      </Btn>
    </div>
  </Modal>;
}

// Capture-review modal. Generates the review URL + offers two paths: show
// the URL/QR on screen for the customer right now OR text it to them.
function ReviewModal({ job, contact, onClose, toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api(`/api/tech/jobs/${job.id}/review-link`, { method:'POST', body:{}}).then(setData).catch(e=>toast(e.message,'error'));
  }, [job.id]);
  const textIt = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/tech/jobs/${job.id}/review-link`, { method:'POST', body:{ sendSms: true }});
      toast(r.smsSent ? `✓ Review link texted to ${contact?.firstName || 'customer'}` : 'No phone on file', r.smsSent?'success':'error');
      onClose();
    } catch(e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={420}>
    <h3 style={{ margin:'0 0 6px' }}>⭐ Get a Review</h3>
    <p style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Best time to ask is right after the job. Two options:</p>
    {data?.reviewUrl && <div style={{ padding:14, background:G.yellow+'22', borderRadius:10, marginBottom:14, textAlign:'center' }}>
      <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Review URL</div>
      <a href={data.reviewUrl} target="_blank" rel="noreferrer" style={{ fontSize:13, color:G.cyan, wordBreak:'break-all' }}>{data.reviewUrl}</a>
      <div style={{ marginTop:10, fontSize:11, color:G.textMuted }}>Show your phone to the customer and have them tap it.</div>
    </div>}
    <Btn v="primary" full onClick={textIt} disabled={busy} style={{ background:G.yellow, borderColor:G.yellow, color:'#000', marginBottom:8 }}>
      {busy ? <Spinner sz={14}/> : `📲 Text link to ${contact?.firstName || 'customer'}`}
    </Btn>
    <Btn v="ghost" full onClick={onClose}>Done</Btn>
  </Modal>;
}

// Customer email composer. Sends from the business email + lands in their
// inbox just like SMS lands in the unified messaging inbox.
function EmailModal({ job, contact, onClose, toast }) {
  const [subject, setSubject] = useState(`Re: ${job?.name || 'your service today'}`);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api(`/api/tech/jobs/${job.id}/email`, { method:'POST', body:{ subject, body }});
      toast(`✓ Email sent to ${contact?.email}`);
      onClose();
    } catch(e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h3 style={{ margin:'0 0 6px' }}>✉ Email {contact?.firstName || 'customer'}</h3>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>To: {contact?.email}</div>
    <Input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Subject" style={{ marginBottom:10 }}/>
    <Textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="Type your message…" style={{ minHeight:140 }}/>
    <div style={{ display:'flex', gap:8, marginTop:10 }}>
      <Btn v="ghost" onClick={onClose} disabled={busy} style={{ flex:1 }}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy || !body.trim()} style={{ flex:2 }}>{busy?<Spinner sz={14}/>:'Send'}</Btn>
    </div>
  </Modal>;
}

// Voice-to-text via Web Speech API. Falls back to a disabled mic with a
// tooltip if the browser doesn't support it (Firefox, older mobile).
function VoiceDictateButton({ onAppend, toast }) {
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const toggle = () => {
    if (!supported) { toast?.('Voice input not supported on this browser — use Chrome or Safari', 'error'); return; }
    if (listening) {
      try { recogRef.current?.stop(); } catch {}
      setListening(false); return;
    }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new Rec();
    r.lang = 'en-US'; r.continuous = false; r.interimResults = false;
    r.onresult = (e) => {
      const txt = Array.from(e.results).map(x => x[0].transcript).join(' ').trim();
      if (txt) onAppend(txt);
    };
    r.onerror = (e) => { toast?.(`Mic error: ${e.error || 'unknown'}`, 'error'); setListening(false); };
    r.onend = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch (e) { setListening(false); toast?.(e.message, 'error'); }
  };
  return <Btn sz="sm" v={listening?'red':'ghost'} onClick={toggle} title={supported ? 'Dictate' : 'Voice not supported'} disabled={!supported}>
    {listening ? '🔴 Listening…' : '🎤'}
  </Btn>;
}

function CollectPaymentModal({ job, onClose, onDone, toast }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState(job?.name || 'Service call');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast('Enter a valid amount','error');
    setBusy(true);
    try {
      const r = await api(`/api/tech/jobs/${job.id}/collect-payment`, { method:'POST', body:{ amount: amt, description, taxRate: 0 }});
      setResult(r);
      if (r.payUrl && navigator.clipboard) { try { navigator.clipboard.writeText(r.payUrl); } catch {} }
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={420}>
    <h2 style={{ margin:'0 0 14px 0', fontSize:18 }}>💳 Collect Payment</h2>
    {result ? <div>
      <div style={{ ...styles.card, padding:16, marginBottom:12, borderColor:G.green+'66', background:G.greenLight }}>
        <div style={{ fontSize:11, color:G.green, fontWeight:700, textTransform:'uppercase' }}>Sent</div>
        <div style={{ fontSize:24, fontWeight:800, color:G.green, margin:'4px 0' }}>${result.total.toFixed(2)}</div>
        <div style={{ fontSize:12, color:G.textMuted }}>Invoice {result.num}{result.smsSent ? ' · SMS sent to customer' : ''}</div>
      </div>
      {result.payUrl && <div style={{ ...styles.card, padding:12, marginBottom:12, fontSize:12, wordBreak:'break-all', lineHeight:1.4 }}>
        <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Payment link (copied)</div>
        <a href={result.payUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>{result.payUrl}</a>
      </div>}
      <Btn v="primary" full onClick={()=>onDone(result)}>Done</Btn>
    </div> : <div>
      <Field label="Amount ($)">
        <Input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" autoFocus inputMode="decimal"/>
      </Field>
      <Field label="What for?">
        <Input value={description} onChange={e=>setDescription(e.target.value)} placeholder="e.g. AC diagnostic + filter swap"/>
      </Field>
      <div style={{ display:'flex', gap:8, marginTop:14 }}>
        <Btn full onClick={onClose}>Cancel</Btn>
        <Btn v="primary" full onClick={submit} disabled={busy || !amount}>{busy ? <Spinner sz={14}/> : 'Send Payment Link'}</Btn>
      </div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:10, lineHeight:1.4 }}>
        Creates an invoice and texts the customer a Stripe payment link. Falls back to in-app portal link if Stripe isn't connected.
      </div>
    </div>}
  </Modal>;
}

function pickFile() {
  return new Promise(resolve => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*'; inp.capture = 'environment';
    inp.onchange = () => resolve(inp.files?.[0] || null);
    inp.click();
  });
}
function readAsDataURL(f) {
  return new Promise((resolve,reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

// Tech's own settings panel. Lives on the bottom nav as "Me". Currently:
// Google Calendar / Outlook two-way sync, so their gym / school-pickup /
// personal events block booking availability, and jobs sync to their phone
// calendar automatically. This is the same connect flow as the owner
// Settings > Calendar tab, just phone-optimized.
function TechMePanel({ toast = () => {} }) {
  const [conns, setConns] = useState({});
  const load = () => api('/api/integrations/connections').then(setConns).catch(()=>{});
  useEffect(() => { load(); }, []);
  const connect = async (provider) => {
    try { const { url } = await api(`/api/integrations/${provider}/connect`); window.location.href = url; }
    catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${provider==='gcal'?'Google Calendar':'Outlook'}?`)) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); load(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };
  const row = (provider, label, icon, color) => {
    const c = conns[provider] || {};
    return <div key={provider} style={{ ...styles.card, padding:14, marginBottom:10, borderColor: c.connected ? color+'66' : G.cardBorder }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:44, height:44, borderRadius:10, background:color+'22', color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{label}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:3, wordBreak:'break-word' }}>
            {c.connected ? `✓ ${c.identity || 'Connected'}` : 'Jobs sync to your calendar. Busy times block bookings.'}
          </div>
        </div>
      </div>
      {c.connected
        ? <Btn sz="sm" v="danger" onClick={()=>disconnect(provider)} full style={{ marginTop:10 }}>Disconnect</Btn>
        : <Btn sz="sm" v="primary" onClick={()=>connect(provider)} full style={{ marginTop:10 }}>Connect {label}</Btn>}
    </div>;
  };
  return <div style={{ padding:'14px 14px 90px' }}>
    <h2 style={{ fontSize:18, margin:'0 0 6px' }}>⚙️ Me</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14, lineHeight:1.5 }}>Connect your personal calendar so jobs show up on your phone + your busy times block bookings.</div>
    {row('gcal',    'Google Calendar',  '🗓️', '#4285f4')}
    {row('outlook', 'Outlook Calendar', '📆', '#0078d4')}
  </div>;
}

// Mobile-friendly training view for the tech app. Shows the same /api/training/my
// data as the owner-side Training screen but with phone-first layout, big tap
// targets, and assignment metadata (due dates, who assigned it) surfaced up front.
function TechTraining({ toast = () => {} }) {
  const [courses, setCourses] = useState(null);
  const [opening, setOpening] = useState(null);
  const load = () => api('/api/training/my').then(setCourses).catch(()=>setCourses([]));
  useEffect(() => { load(); }, []);
  if (courses === null) return <div style={{ padding:24, textAlign:'center' }}><Spinner sz={28}/></div>;
  if (!courses.length) return <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:14 }}>No training assigned yet. Ask your owner to assign you a course.</div>;
  return <div style={{ padding:'14px 14px 90px' }}>
    <h2 style={{ fontSize:18, margin:'0 0 10px' }}>🎓 My Training</h2>
    {courses.map(c => {
      const total = (c.sections||[]).length;
      const done = c.my_progress?.sections_completed?.length || 0;
      const pct = c.my_progress?.progress_pct || 0;
      const completed = c.my_progress?.completed_at;
      const due = c.my_progress?.due_at;
      const isOverdue = due && !completed && new Date(due) < new Date();
      return <div key={c.id} onClick={()=>setOpening(c)} style={{ ...styles.card, padding:14, marginBottom:10, cursor:'pointer', borderColor: completed?G.green+'66' : isOverdue?G.red+'66' : G.cardBorder }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
          <Badge c={G.cyan} bg={G.cyan+'22'}>{c.category || 'General'}</Badge>
          {completed ? <Badge c={G.green} bg={G.greenLight}>✓ Done</Badge>
            : isOverdue ? <Badge c={G.red} bg={G.redLight}>⏰ Overdue</Badge>
            : due ? <Badge c={G.yellow} bg={G.yellowLight}>Due {new Date(due).toLocaleDateString()}</Badge>
            : null}
        </div>
        <div style={{ fontWeight:700, fontSize:16 }}>{c.title}</div>
        {c.description && <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>{c.description}</div>}
        <div style={{ marginTop:10, height:8, background:G.input, borderRadius:4, overflow:'hidden' }}>
          <div style={{ width:`${pct}%`, height:'100%', background: completed?G.green:G.purple, transition:'.3s' }}/>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>{done}/{total} sections · {pct}%</div>
      </div>;
    })}
    {opening && <TechCourseViewer course={opening} onClose={()=>setOpening(null)} onUpdate={load} toast={toast}/>}
  </div>;
}

function TechCourseViewer({ course, onClose, onUpdate, toast }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [completed, setCompleted] = useState(course.my_progress?.sections_completed || []);
  const sec = course.sections[sectionIdx];
  const isLast = sectionIdx >= course.sections.length - 1;
  const markDone = async () => {
    try {
      const r = await api(`/api/training/courses/${course.id}/section-complete`, { method:'POST', body:{ sectionIdx }});
      setCompleted(r.completed);
      if (r.isDone) { toast('🎓 Course complete!'); onUpdate?.(); onClose(); return; }
      if (!isLast) setSectionIdx(sectionIdx + 1);
      onUpdate?.();
    } catch(e) { toast(e.message,'error'); }
  };
  if (!sec) return null;
  return <Modal open onClose={onClose} width={520}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
      <h2 style={{ margin:0, fontSize:18 }}>{course.title}</h2>
      <span style={{ fontSize:12, color:G.textMuted }}>{sectionIdx+1}/{course.sections.length}</span>
    </div>
    <div style={{ ...styles.card, padding:14, marginBottom:14 }}>
      <strong style={{ fontSize:15 }}>{sec.title || `Section ${sectionIdx+1}`}</strong>
      {sec.type === 'text' && sec.content && <div style={{ marginTop:8, fontSize:14, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{sec.content}</div>}
      {sec.type === 'image' && sec.url && <img src={sec.url} style={{ maxWidth:'100%', borderRadius:8, marginTop:8 }}/>}
      {sec.type === 'video' && sec.url && <video src={sec.url} controls style={{ width:'100%', maxHeight:300, borderRadius:8, marginTop:8 }}/>}
      {sec.type === 'pdf' && sec.url && <a href={sec.url} target="_blank" rel="noopener noreferrer" style={{ display:'block', marginTop:8, color:G.cyan }}>📑 Open PDF ↗</a>}
    </div>
    <div style={{ display:'flex', gap:8, justifyContent:'space-between' }}>
      <Btn onClick={()=>setSectionIdx(Math.max(0, sectionIdx-1))} disabled={sectionIdx===0}>← Prev</Btn>
      <Btn v="primary" onClick={markDone}>
        {completed.includes(sectionIdx) ? 'Next →' : (isLast ? 'Finish Course' : '✓ Mark Done + Next')}
      </Btn>
    </div>
  </Modal>;
}
